import type { NextPageContext } from 'next';
import cookies from 'nookies';
/* note: it would be nice to remove this for native node uuid, but we are still
calling this from the client in getInitialProps at times so its isomorphic */
import { v4 as uuidv4 } from 'uuid';

const anonIdRegexp = new RegExp('(^| )ajs_anonymous_id=([^;]+)');
const doubleQuoteRegex = /"/g;
export const parseId = (str: string): string =>
    str.replace(doubleQuoteRegex, '');

export const ANON_ID_COOKIE_NAME = 'ajs_anonymous_id';
const isVercelPreview = process.env['NEXT_PUBLIC_VERCEL_ENV'] === 'preview';
const domain = isVercelPreview ? undefined : '.zoopla.co.uk';

/**
 * Retrieve the anonId of the user on both client and server.
 * We must handle several cases:
 * 1. user has already visited the site and an anonId cookie is set. If this is
 * the case then read the cookie (from either request context for SSR or client
 * document for static pages).
 * 2. user has not visited before and a _middleware has set an anonId cookie
 * prior to the request being handled, in which case we extract the anonId from
 * the Set-Cookie header.
 * 3. user has not visited before and there is not a _middleware in place i.e
 * it is a SSR page. In which case we will generate a new anonId and write out
 * to cookies to persist.
 * 4. We are rendering a static page at runtime with ISR in which case there is
 * no ctx.res and therefore we do not get an anonId, in this scenario we use
 * path params to extract Optimizely state and rely on client init to match...
 *
 * In scenario 2/3/4 a Set-Cookie header will be picked up in the browser and
 * thus if this function is called client side we will effectively go to opt 1
 *
 * This is a bit novel but we NEED to know the anonId at point of render.
 * Segment will pick up the ajs_anonymous_id value and use it when setting an
 * anonymous analytics id. We also pass its value to Optimizely as a user id.
 * Thus Optimizely and Segment are in sync.
 */
export function getAnonId(ctx: Pick<NextPageContext, 'req' | 'res'>) {
    const allCookies = cookies.get(ctx);
    const existingAnonId = allCookies[ANON_ID_COOKIE_NAME];

    if (existingAnonId) {
        return parseId(existingAnonId);
    }

    if (ctx.res) {
        /* remember if we have access to ctx.res we must be in GSSP or GIP
        method and therefore this should only be called once */
        let setCookieHeaderStr: string;

        /* if we are serverside and _middleware has added the cookie header pull and parse from there */
        const setCookieHeader = ctx.res?.getHeader?.('Set-Cookie');
        if (Array.isArray(setCookieHeader)) {
            setCookieHeaderStr = setCookieHeader.join(' ');
        } else if (typeof setCookieHeader === 'string') {
            setCookieHeaderStr = setCookieHeader;
        } else if (setCookieHeader) {
            setCookieHeaderStr = setCookieHeader?.toString();
        } else {
            setCookieHeaderStr = '';
        }
        const matches = setCookieHeaderStr.match(anonIdRegexp);
        if (matches && matches.length > 1) {
            /* retrieve and return the last occurring match */
            return parseId(matches[matches.length - 1]);
        }

        /* _middleware has not generated the anonId so we should generate one now and write out to the response */
        const anonId = uuidv4();
        cookies.set(ctx, ANON_ID_COOKIE_NAME, anonId, {
            maxAge: 365 * 24 * 60 * 60,
            sameSite: 'lax',
            domain
        });
        return anonId;
    }

    /* no ctx.res, this function should only be called within data-fetching
    methods so we must be in GetStaticProps, in this case we can rely on
    _middleware having Set-Cookie and we will pick up on client render */
    return;
}
